import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Divider, FormInputText, InputLabel, PhoneNumberInput } from '@hopskipdrive/react-components';
import { FormA11yErrorAnnouncer } from '@rideiq/shared/features/FormA11yErrorAnnouncer';

import { useFlags } from '../../../contexts/LDContext';
import { JobTitleOption } from '../../hooks/useJobTitles';

import { JobTitleFormField } from './JobTitleFormField';
import { RoleFormField } from './RoleFormField';
import { FormWrapper, InfoGrid, RolesInfoGrid } from './UserForm.styles';

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  mobilePhoneNumber: string;
  role: string;
  initialRole: string;
  jobTitle: string;
  location: AccountLocation | null;
};

interface UserInfoFormProps {
  rolesInfo: {
    roleOptions: { value: string; label: string }[];
    roleDescriptionMap: Map<string | null, string>;
    isFetching: boolean;
  };
  jobTitlesInfo?: {
    jobTitleOptions: JobTitleOption[];
    isFetching: boolean;
  };
  handleSubmit: (data) => void;
  defaultRole?: Nullable<string>;
  addOnsiteStaffForm?: boolean;
}

export const UserForm: FC<UserInfoFormProps> = ({
  rolesInfo,
  jobTitlesInfo = { jobTitleOptions: [], isFetching: false },
  handleSubmit,
  defaultRole,
  addOnsiteStaffForm,
}) => {
  const methods = useFormContext<FormData>();
  const { jobTitleDropdownEnabled } = useFlags();

  const {
    control,
    watch,
    formState: { errors },
  } = methods;

  const selectedRole = watch('role');

  return (
    <>
      <FormA11yErrorAnnouncer errors={errors} />
      <form id="user-info-form" onSubmit={methods.handleSubmit(handleSubmit)}>
        <FormWrapper>
          <InfoGrid>
            <InputLabel fieldtype="required" htmlFor="firstName">
              First Name
            </InputLabel>
            <Controller
              control={control}
              name="firstName"
              defaultValue=""
              render={({ field: { value, onChange } }) => (
                <FormInputText
                  variant="outlined"
                  name="firstName"
                  value={value}
                  id="firstName"
                  data-testid="firstName"
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  onChange={onChange}
                />
              )}
            />
            <InputLabel fieldtype="required" htmlFor="lastName">
              Last Name
            </InputLabel>
            <Controller
              control={control}
              name="lastName"
              defaultValue=""
              render={({ field: { value, onChange } }) => (
                <FormInputText
                  variant="outlined"
                  id="lastName"
                  name="lastName"
                  value={value}
                  data-testid="lastName"
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  onChange={onChange}
                />
              )}
            />

            <InputLabel fieldtype="required" htmlFor="email">
              Email
            </InputLabel>
            <Controller
              control={control}
              name="email"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <FormInputText
                  variant="outlined"
                  id="email"
                  name="email"
                  value={value}
                  data-testid="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  onChange={onChange}
                />
              )}
            />
            <InputLabel htmlFor="mobilePhoneNumber">Phone Number</InputLabel>
            <Controller
              control={methods.control}
              name="mobilePhoneNumber"
              render={({ field: { onChange, value, name } }) => (
                <PhoneNumberInput
                  variant="outlined"
                  name={name}
                  fullWidth
                  id={name}
                  data-testid={name}
                  onValueChange={onChange}
                  initialValue={value}
                  error={!!errors.mobilePhoneNumber}
                  helperText={errors.mobilePhoneNumber?.message}
                />
              )}
            />
            {jobTitleDropdownEnabled ? (
              <JobTitleFormField
                jobTitleOptions={jobTitlesInfo.jobTitleOptions}
                isFetching={jobTitlesInfo.isFetching}
                isRequired={selectedRole === 'onsite_staff'}
              />
            ) : (
              !!rolesInfo.roleOptions.length &&
              selectedRole === 'onsite_staff' && (
                <>
                  <InputLabel fieldtype="required" htmlFor="jobTitle">
                    Job Title
                  </InputLabel>
                  <Controller
                    control={control}
                    name="jobTitle"
                    defaultValue=""
                    render={({ field: { value, onChange } }) => (
                      <FormInputText
                        variant="outlined"
                        id="jobTitle"
                        name="jobTitle"
                        value={value}
                        data-testid="jobTitle"
                        fullWidth
                        error={!!errors.jobTitle}
                        helperText={errors.jobTitle?.message}
                        onChange={onChange}
                      />
                    )}
                  />
                </>
              )
            )}
          </InfoGrid>
          <Divider />
          <RolesInfoGrid>
            <RoleFormField
              roleOptions={rolesInfo.roleOptions}
              roleDescriptionMap={rolesInfo.roleDescriptionMap}
              isFetching={rolesInfo.isFetching}
              defaultRole={defaultRole}
              addOnsiteStaffForm={addOnsiteStaffForm}
            />
          </RolesInfoGrid>
        </FormWrapper>
      </form>
    </>
  );
};
